import React, { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { addHours, startOfHour } from 'date-fns';
import { Trash2Icon, ChevronDownIcon } from 'lucide-react';

import { queryClient } from 'api';
import { CapacityType, EditableSession, EventFormat } from 'types/event.types';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { Button } from '@epoch-ui/components/Button/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuContent
} from '@epoch-ui/components/DropdownMenu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter
} from '@epoch-ui/components/Sheet/sheet';
import { CreateDraftSessionPayload, eventSessionsApi } from '@event-editor/api/eventSessions';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import {
  useSchedulePublishModal,
  useCreateDraftSessionSheet
} from '@event-editor/hooks/eventEditorState';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { isStartTimeBeforeScheduledPublish } from '@event-editor/utils';
import { convertSendAtToDBValue } from 'pages/dashboard/overview/events/schemas/commonUtils';
import { useSessionTimeChangeValidations } from '@event-editor/components/EditEventDetailsSheet/validateEventTimeChanges';
import {
  TimeChangeConfirmModal,
  TimeChangeConfirmModalType
} from '@event-editor/components/EditEventDetailsSheet/TimeChangeConfirmModal';

import { EventSessionForm } from '../../tabs/EditEventOverview/components/EventSessions/components/EventSessionForm';
import {
  getCreateDraftSessionSchema,
  getUpdateSessionSchema,
  formValuesToPublishSessionApiPayload,
  CreateDraftSessionFormValues,
  UpdateSessionFormValues
} from '../../tabs/EditEventOverview/components/EventSessions/components/schemas';
import { resetSessionTimeFields } from '../../tabs/EditEventOverview/components/EventSessions/components/sessionTimeUtils';

const CreateDraftSessionSheet: React.FC = () => {
  const event = useEditableEvent();
  const sheetState = useCreateDraftSessionSheet();
  const { showToast } = useToaster();
  const schedulePublishModal = useSchedulePublishModal();
  const [isTimeChangeConfirmModalOpen, setIsTimeChangeConfirmModalOpen] = useState(false);
  const [pendingPublishData, setPendingPublishData] = useState<UpdateSessionFormValues | null>(
    null
  );
  const eventTimezoneCode = event.timezoneCode!;

  const form = useForm<CreateDraftSessionFormValues>({
    resolver: zodResolver(getCreateDraftSessionSchema(eventTimezoneCode)),
    mode: 'onChange',
    defaultValues: {
      title: '',
      description: '',
      startDateTime: addHours(startOfHour(new Date()), 1),
      endDateTime: addHours(startOfHour(new Date()), 2),
      allDay: false,
      targetAttendance: null,
      capacity: {
        maxAttendees: null,
        capacityType: CapacityType.UNLIMITED
      }
    }
  });

  const [updatedStartDateTime, updatedEndDateTime] = form.watch(['startDateTime', 'endDateTime']);

  const timeChangeValidations = useSessionTimeChangeValidations(
    undefined,
    updatedStartDateTime,
    updatedEndDateTime
  );
  const hasTimeChangeValidations = timeChangeValidations.length > 0;

  // Check if start time is before scheduled publish time (component-level validation)
  const hasStartTimeBeforeScheduledPublishError = useMemo(() => {
    if (!updatedStartDateTime || !eventTimezoneCode) {
      return false;
    }
    const startDateTimeUTC = convertSendAtToDBValue(updatedStartDateTime, eventTimezoneCode);
    return isStartTimeBeforeScheduledPublish(startDateTimeUTC, event.publishSchedule);
  }, [updatedStartDateTime, eventTimezoneCode, event.publishSchedule]);

  const createDraftSessionMutation = useMutation({
    mutationFn: eventSessionsApi.createDraft,
    onSuccess: (results) => {
      if (results.ok) {
        showToast('Session created');
        queryClient.invalidateQueries({
          queryKey: eventEditorQueryKeys.eventSessions(event.id!)
        });
        queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });
        form.reset();
        sheetState.close();
      } else {
        showToast('Failed to create session', { variant: ToasterVariant.ERROR });
      }
    },
    onError: () => {
      showToast('Failed to create session', { variant: ToasterVariant.ERROR });
    }
  });

  const publishSessionMutation = useMutation({
    mutationFn: eventSessionsApi.publish,
    onSuccess: (results) => {
      if (results.ok) {
        showToast('Session published');

        // Refresh event and sessions from API
        queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.eventSessions(event.id!) });
        queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });

        // Close the sheet
        sheetState.close();
      } else {
        showToast('Failed to publish session', { variant: ToasterVariant.ERROR });
      }
    },
    onError: () => {
      showToast('Failed to publish session', { variant: ToasterVariant.ERROR });
    }
  });

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        form.reset();
        sheetState.close();
      } else {
        sheetState.open();
      }
    },
    [form, sheetState]
  );

  const handleCreateDraftSession = useCallback(
    (data: CreateDraftSessionFormValues) => {
      createDraftSessionMutation.mutate({
        eventId: event.id!,
        payload: formValuesToApiPayload(data, eventTimezoneCode)
      });
    },
    [event.id, eventTimezoneCode]
  );

  const handleCreateAndPublishSession = useCallback(
    (data: CreateDraftSessionFormValues) => {
      // Validate data for publish using `UpdateSessionSchema` to ensure all required fields are present
      const { error: publishValidationErrors, data: validatedData } = getUpdateSessionSchema(
        { hasEnded: false } as EditableSession,
        eventTimezoneCode
      ).safeParse(data);

      if (publishValidationErrors) {
        // Set errors for form
        publishValidationErrors.issues.forEach((issue) => {
          form.setError(issue.path.join('.') as keyof CreateDraftSessionFormValues, {
            message: issue.message,
            type: issue.code
          });
        });
      } else {
        if (hasTimeChangeValidations) {
          setPendingPublishData(validatedData);
          setIsTimeChangeConfirmModalOpen(true);
          return;
        }
        proceedWithPublish(validatedData);
      }
    },
    [event.id, hasTimeChangeValidations]
  );

  const proceedWithPublish = (validatedData: UpdateSessionFormValues): void => {
    publishSessionMutation.mutate({
      eventId: event.id!,
      ...formValuesToPublishSessionApiPayload(validatedData, eventTimezoneCode)
    });
  };

  const handleResetTime = useCallback((): void => {
    resetSessionTimeFields(form);
    setIsTimeChangeConfirmModalOpen(false);
    setPendingPublishData(null);
  }, [form]);

  const handleConfirmTimeChange = useCallback(async (): Promise<void> => {
    setIsTimeChangeConfirmModalOpen(false);

    if (!pendingPublishData) {
      return;
    }

    proceedWithPublish(pendingPublishData);
    setPendingPublishData(null);
  }, [pendingPublishData]);

  const isCreating = createDraftSessionMutation.isPending || publishSessionMutation.isPending;

  return (
    <Sheet open={sheetState.isOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        className="tw-m-2 tw-flex tw-h-[calc(100vh-16px)] tw-w-[480px] tw-flex-col tw-gap-0 tw-rounded-3xl tw-p-0 sm:tw-max-w-[480px]"
        aria-describedby=""
      >
        <SheetHeader className="tw-p-6">
          <SheetTitle className="tw-mb-0">Add session</SheetTitle>
        </SheetHeader>

        <div className="tw-flex tw-flex-grow tw-flex-col tw-gap-4 tw-overflow-auto tw-pb-6">
          <EventSessionForm
            id="create-draft-session-form"
            form={form}
            hasStartTimeBeforeScheduledPublishError={hasStartTimeBeforeScheduledPublishError}
            onUpdateScheduleClick={() => schedulePublishModal.open({ isReschedule: true })}
          />
        </div>

        <hr className="tw-my-0 tw-w-full tw-border-gray-300" />
        <SheetFooter className="tw-flex tw-w-full tw-flex-shrink-0 tw-justify-between tw-p-4">
          {event.isPublished ? (
            // Actions to add/publish session in a published event
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  className="tw-rounded-xl"
                  disabled={isCreating || hasStartTimeBeforeScheduledPublishError}
                  loading={isCreating}
                >
                  Add session <ChevronDownIcon size={16} className="tw-ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem
                  onClick={() =>
                    form.handleSubmit(handleCreateDraftSession, (errors) => {
                      console.error(errors);
                    })()
                  }
                >
                  Save as draft
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    form.handleSubmit(handleCreateAndPublishSession, (errors) => {
                      console.error(errors);
                    })()
                  }
                >
                  Publish
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            // Action to save session as draft in an unpublished event
            <Button
              form="create-draft-session-form"
              size="sm"
              className="tw-rounded-xl"
              disabled={isCreating || hasStartTimeBeforeScheduledPublishError}
              loading={isCreating}
              onClick={() =>
                form.handleSubmit(handleCreateDraftSession, (errors) => {
                  console.error(errors);
                })()
              }
            >
              Done
            </Button>
          )}
          <Button variant="ghost" size="sm" className="tw-rounded-xl tw-text-negative-foreground">
            <Trash2Icon size={16} className="tw-mr-1" />
            Delete
          </Button>
        </SheetFooter>

        <TimeChangeConfirmModal
          open={isTimeChangeConfirmModalOpen}
          onOpenChange={setIsTimeChangeConfirmModalOpen}
          timeChangeValidations={timeChangeValidations}
          type={TimeChangeConfirmModalType.CONFIRM}
          isSession
          onReschedule={handleResetTime}
          onConfirm={handleConfirmTimeChange}
        />
      </SheetContent>
    </Sheet>
  );
};

export { CreateDraftSessionSheet };

const formValuesToApiPayload = (
  data: CreateDraftSessionFormValues,
  timezoneCode: string
): CreateDraftSessionPayload => ({
  title: data.title,
  description: data.description,
  startDatetime: data.startDateTime
    ? convertSendAtToDBValue(data.startDateTime, timezoneCode)
    : undefined,
  endDatetime: data.endDateTime
    ? convertSendAtToDBValue(data.endDateTime, timezoneCode)
    : undefined,
  allDay: data.allDay,
  isInPerson: data.format === EventFormat.IN_PERSON || data.format === EventFormat.HYBRID,
  isVirtual: data.format === EventFormat.VIRTUAL || data.format === EventFormat.HYBRID,
  virtualLink: data.virtualLink ?? undefined,
  zoomMeetingId: data.zoomMeetingId ?? undefined,
  googleMeetingId: data.googleMeetingId ?? undefined,
  inPersonLocation: data.inPersonLocation ?? undefined,
  maxAttendees: data.capacity?.maxAttendees ?? undefined,
  targetAttendance: data.targetAttendance ?? undefined,
  plusOneConfig: data.plusOneConfig
    ? {
        allowPlusOnes: data.plusOneConfig.allowPlusOnes,
        maxPlusOnes: data.plusOneConfig.maxPlusOnes,
        collectQuestionnaireResponses: data.plusOneConfig.collectQuestionnaireResponses
      }
    : undefined
});
